import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { Button, IconButton, MobileStepper, Typography, useTheme } from '@mui/material';

import { TOGGLE_SHOW_ONBOARD, LOCAL_SHOW_ONBOARDS } from '../../shared';
import ButtonContainer from '../../components/ButtonContainer';
import CaretLeftIcon from '../../icons/CaretLeftIcon';
import CaretRightIcon from '../../icons/CaretRightIcon';

import { StyledOnboardingSlideControls } from './Onboarding.styled';

const Onboarding = ({ initialIndex, slides, name, onboardingUrl, mainPageUrl }) => {
  const history = useHistory();

  const theme = useTheme();

  const initialIndexValue = parseInt(initialIndex, 10);
  const integerInitialIndex = Number.isNaN(Number(initialIndexValue)) ? 1 : initialIndexValue;
  const positiveIntitalIndex = integerInitialIndex <= 0 ? 1 : integerInitialIndex;
  const validInitialIndex = positiveIntitalIndex > slides.length ? 1 : positiveIntitalIndex;

  const [skipOnboard] = useMutation(TOGGLE_SHOW_ONBOARD, {
    variables: {
      name,
      show: false,
    },
    refetchQueries: [{ query: LOCAL_SHOW_ONBOARDS }],
  });

  const [currentSlideNumber, setCurrentSlideNumber] = useState(validInitialIndex - 1);

  const goToSlide = (index) => {
    const newSlideIndex = (index + slides.length) % slides.length;
    setCurrentSlideNumber(newSlideIndex);
    history.push(`${onboardingUrl}/${newSlideIndex + 1}`);
  };

  const onSkipButtonClick = () => {
    window.localStorage.setItem(name, 'skip');
    skipOnboard();
    history.push(mainPageUrl);
  };

  const isLastSlide = slides.length === currentSlideNumber + 1;

  return (
    <section aria-roledescription="carousel" aria-label="Onboarding">
      <div
        id="onboarding_slides"
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide ${currentSlideNumber + 1} of ${slides.length}`}
      >
        {slides.at?.(currentSlideNumber)?.slide}
      </div>

      <StyledOnboardingSlideControls>
        <MobileStepper
          variant="dots"
          steps={slides.length}
          position="static"
          activeStep={currentSlideNumber}
          backButton={
            currentSlideNumber !== 0 ? (
              <IconButton
                size="medium"
                onClick={() => goToSlide(currentSlideNumber - 1)}
                aria-label="Previous slide"
                aria-controls="onboarding_slides"
              >
                <CaretLeftIcon stroke={theme.palette.main.professional_blue} />
              </IconButton>
            ) : (
              <div />
            )
          }
          nextButton={
            !isLastSlide ? (
              <IconButton
                size="medium"
                onClick={() => goToSlide(currentSlideNumber + 1)}
                aria-label="Next slide"
                aria-controls="onboarding_slides"
              >
                <CaretRightIcon stroke={theme.palette.main.professional_blue} />
              </IconButton>
            ) : (
              <div />
            )
          }
        />
      </StyledOnboardingSlideControls>

      <ButtonContainer>
        <Button variant={isLastSlide ? 'contained' : 'outlined'} onClick={onSkipButtonClick}>
          <Typography variant="buttons">
            {slides?.at?.(currentSlideNumber)?.buttonText || ''}
          </Typography>
        </Button>
      </ButtonContainer>
    </section>
  );
};

Onboarding.propTypes = {
  /** The name of your onboard */
  name: PropTypes.string.isRequired,
  /** It's an array with all the onboard pages */
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      slide: PropTypes.node,
      buttonText: PropTypes.string,
    })
  ).isRequired,
};

export default Onboarding;
